"use client";

import { StyledLink } from "@/components/StyledLink";
import {
  Box,
  Container,
  Typography,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Stack,
  Link,
  CircularProgress,
  styled,
  List,
  ListItem,
  Pagination, // Import Pagination component
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandIcon as ExpandMore,
  ExternalLink,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { apiRemote, IUserRoundStatus } from "@/api";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAlert } from "@/contexts/AlertContext";
import { useUser } from "@/contexts/UserContext";

const theme = createTheme({
  palette: {
    primary: {
      main: "#007bff",
    },
    secondary: {
      main: "#6c757d",
    },
    success: {
      main: "#28a745",
    },
    warning: {
      main: "#ffc107",
    },
    tcc: {
      main: "#6f42c1",
    },
    background: {
      default: "#f4f7f9",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: "Roboto, sans-serif",
    h1: {
      fontSize: "28px",
      fontWeight: 700,
      color: "#333",
    },
    h3: {
      fontSize: "18px",
      fontWeight: 500,
    },
    h4: {
      fontSize: "16px",
      fontWeight: 500,
    },
    body1: {
      fontSize: "16px",
    },
    body2: {
      fontSize: "14px",
    },
    caption: {
      fontSize: "12px",
    },
  },
  shape: {
    borderRadius: 2,
  },
});

const StatusStep = styled(Box)(({ theme, completed, active }) => ({
  flex: 1,
  textAlign: "center",
  fontSize: "12px",
  color: theme.palette.secondary.main,
  position: "relative",
  "&::before": {
    content: '""',
    display: "block",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#e9ecef",
    border: "2px solid #ced4da",
    margin: "0 auto 8px",
    ...(completed && {
      backgroundColor: "#6f42c1",
      borderColor: "#6f42c1",
    }),
    ...(active && {
      backgroundColor: "#fff",
      borderColor: "#6f42c1",
    }),
  },
  "&:not(:last-child)::after": {
    content: '""',
    position: "absolute",
    top: "12px",
    left: "50%",
    width: "100%",
    height: "2px",
    backgroundColor: "#ced4da",
    zIndex: -1,
  },
  ...(completed && {
    "&:not(:last-child)::after": {
      backgroundColor: "#6f42c1",
    },
  }),
}));

const WidgetHeader = ({ title, viewAllLink, titleColor }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #eee",
      paddingBottom: 1.5,
      marginBottom: 2,
    }}
  >
    <Typography variant="h5" sx={{ color: titleColor || "text.primary" }}>
      {title}
    </Typography>
    {viewAllLink && (
      <Link
        href={viewAllLink}
        sx={{
          fontSize: "14px",
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Go to Community →
      </Link>
    )}
  </Box>
);

// Di chuyển CommunityHighlightsWidget vào trong CommunityHighLightWidget để truy cập state
// Thay vì là một functional component riêng biệt, nó sẽ là một phần của component chính
// hoặc bạn có thể truyền props cần thiết.
// const CommunityHighlightsWidget = () => (...)

export default function CommunityHighLightWidget() {
  const navigate = useNavigate();
  const { user } = useUser();
  const alert = useAlert();

  const [loading, setLoading] = useState<boolean>(false);
  const [recentComm, setRecentComm] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1); // State cho trang hiện tại
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [totalPages, setTotalPages] = useState<number>(1); // Tổng số trang từ server

  const loadCommunityHighlight = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRemote.getRecentCommunity(
        currentPage,
        itemsPerPage
      );
      if (response.data && Array.isArray(response.data)) {
        setRecentComm(response.data);
        setTotalPages(response.total_pages); // Cập nhật tổng số trang
      } else {
        setRecentComm([]);
        setTotalPages(1); // Reset tổng số trang nếu không có dữ liệu
      }
    } catch (error) {
      console.error("Error:", error);
      setRecentComm([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [user, currentPage, itemsPerPage]); // Thêm user vào dependency array nếu apiRemote phụ thuộc vào user

  useEffect(() => {
    loadCommunityHighlight();
  }, [loadCommunityHighlight, currentPage, itemsPerPage]); // Thêm currentPage và itemsPerPage vào dependency array

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper
        sx={{
          p: 3,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[1],
          bgcolor: "background.paper",
          mt: 2,
        }}
      >
        <WidgetHeader
          title="Community Highlights"
          viewAllLink={process.env.API_COMM + "/groups/tcc-2025-champions/forum/"}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : recentComm.length > 0 ? (
          <List disablePadding>
            {recentComm.map((item) => (
              <ListItem
                key={item.id}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}
              >
                <Link
                  href={item.link}
                  sx={{
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Typography variant="body1" component="span" sx={{ fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    By {item.author.name} on {item.date}
                  </Typography>
                </Link>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            No community highlights to display.
          </Typography>
        )}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </ThemeProvider>
  );
}